import {
  makeCaptcha,
  PET,
  SACRIFICE_COUNT,
  SACRIFICE_POOL
} from './passwordData';
import {
  nextRevealed,
  PASSWORD_RULES,
  type PasswordContext,
  triggersFor
} from './passwordRules';

/**
 * All of The Password Game's mutable state in one reducer. It lives here rather
 * than in the component because the interactive rules (fire eating characters,
 * the chick dying) are *transitions*, and expressing them as a pure reducer
 * keeps them testable and stops the component growing a pile of interdependent
 * useStates.
 */
export type PasswordState = {
  password: string;
  /** High-water mark of rules on screen; rules never un-reveal. */
  revealed: number;
  captcha: string;
  sacrificed: string[];
  petHatched: boolean;
  status: 'playing' | 'won' | 'lost';
  lossReason: string | null;
};

/** Date-derived values the reducer needs but must not read from the clock itself. */
export type PasswordExtras = { moonEmoji: string; dayOfMonth: number };

export type PasswordAction =
  | { type: 'type'; value: string }
  | { type: 'sacrifice'; letters: string[] }
  | { type: 'insert'; text: string }
  /** One beat of the fire clock. `random` is injected so tests are deterministic. */
  | { type: 'tick'; random?: () => number }
  | { type: 'restart' };

export const FIRE = '🔥';

/** How many characters catch light each time the fire is stoked. */
const FIRE_SIZE = 2;

const RULE_COUNT = PASSWORD_RULES.length;

export const initialState: PasswordState = {
  password: '',
  revealed: 1,
  captcha: '',
  sacrificed: [],
  petHatched: false,
  status: 'playing',
  lossReason: null
};

export const contextFor = (
  state: PasswordState,
  extras: PasswordExtras
): PasswordContext => ({
  password: state.password,
  captcha: state.captcha,
  sacrificed: state.sacrificed,
  moonEmoji: extras.moonEmoji,
  dayOfMonth: extras.dayOfMonth
});

const allPass = (ctx: PasswordContext, upTo: number) =>
  PASSWORD_RULES.slice(0, upTo).every((rule) => rule.test(ctx));

/**
 * Advance the revealed rules, arm any newly required side effects, and decide
 * whether the run is won. Every action that changes the password funnels
 * through here so the unlock path is identical for typing, inserting and fire.
 */
const advance = (
  state: PasswordState,
  extras: PasswordExtras,
  random: () => number
): PasswordState => {
  const revealed = nextRevealed(contextFor(state, extras), state.revealed);
  const triggers = triggersFor(revealed);

  let next: PasswordState = { ...state, revealed };

  // A CAPTCHA is issued once, the first time its rule appears.
  if (triggers.includes('captcha') && next.captcha === '') {
    next = { ...next, captcha: makeCaptcha(random) };
  }

  // The chick is inserted for the player — they only have to keep it alive.
  if (triggers.includes('pet') && !next.petHatched) {
    next = { ...next, petHatched: true, password: next.password + PET };
  }

  const won =
    next.revealed === RULE_COUNT &&
    allPass(contextFor(next, extras), RULE_COUNT);

  return won ? { ...next, status: 'won' } : next;
};

export const makeReducer =
  (extras: PasswordExtras) =>
  (state: PasswordState, action: PasswordAction): PasswordState => {
    // A dead chick is final; only a restart gets you out of it.
    if (state.status === 'lost' && action.type !== 'restart') return state;

    switch (action.type) {
      case 'type': {
        if (state.petHatched && !action.value.includes(PET)) {
          return {
            ...state,
            password: action.value,
            status: 'lost',
            lossReason: 'You deleted the chick. It did not survive.'
          };
        }

        return advance(
          { ...state, password: action.value, status: 'playing' },
          extras,
          Math.random
        );
      }

      case 'insert':
        return advance(
          { ...state, password: state.password + action.text },
          extras,
          Math.random
        );

      case 'sacrifice':
        return advance(
          { ...state, sacrificed: action.letters.slice(0, SACRIFICE_COUNT) },
          extras,
          Math.random
        );

      case 'tick': {
        const random = action.random ?? Math.random;
        if (!triggersFor(state.revealed).includes('fire')) return state;

        const chars = [...state.password];
        if (chars.length === 0) return state;

        // Anything still alight from the last beat burns away now.
        if (chars.includes(FIRE)) {
          return advance(
            { ...state, password: chars.filter((c) => c !== FIRE).join('') },
            extras,
            random
          );
        }

        // Otherwise set light to a couple of random positions.
        const positions = new Set<number>();
        for (let i = 0; i < FIRE_SIZE; i += 1) {
          positions.add(Math.floor(random() * chars.length));
        }

        return advance(
          {
            ...state,
            password: chars
              .map((char, index) => (positions.has(index) ? FIRE : char))
              .join('')
          },
          extras,
          random
        );
      }

      case 'restart':
        return initialState;

      default:
        return state;
    }
  };

export const sacrificeOptions = () => [...SACRIFICE_POOL];
